import z from "zod";
import { ScheduleRecurrence } from "./scheduleRecurrence";
import { Weekday as WeekdayType } from "./weekday";
import moment from "moment";

/** Daily repeat type. */
export enum DailyRepeatType {
    DayNumber = "dayNumber",
    Weekdays = "weekdays",
    Weekends = "weekend",
}

/** Monthly repeat type. */
export enum MonthlyRepeatType {
    Weekday = "weekday",
    Day = "day",
}

/** Recurrent end type. */
export enum RecurrenceEndType {
    Never = "never",
    Date = "date",
}

export const recurrenceRuleValidationSchema = z.object({
    recurrence: z.enum(ScheduleRecurrence),
    hourly: z.object({
        hourNumber: z.number(),
    }),
    daily: z.object({
        type: z.enum(DailyRepeatType),
        dayNumber: z.number(),
    }),
    weekly: z.object({
        weekNumber: z.number(),
        days: z.array(z.enum(WeekdayType)),
    }),
    monthly: z.object({
        type: z.enum(MonthlyRepeatType),
        weekday: z.object({
            weekdayNumber: z.number(),
            weekday: z.enum(WeekdayType),
            monthNumber: z.number(),
        }),
        day: z.object({
            dayNumber: z.number(),
            monthNumber: z.number(),
        }),
    }),
    end: z.object({
        type: z.enum(RecurrenceEndType),
        date: z.date(),
    }),
});

export type RecurrenceRule = z.infer<typeof recurrenceRuleValidationSchema>;

/**
 * Gets default recurrence rule.
 * @param startDate Start date of recurrence.
 */
export function getDefaultRecurrenceRule(startDate: Date = new Date()): RecurrenceRule {
    return {
        recurrence: ScheduleRecurrence.NoRepeat,
        hourly: {
            hourNumber: 1,
        },
        daily: {
            type: DailyRepeatType.DayNumber,
            dayNumber: 1,
        },
        weekly: {
            weekNumber: 1,
            days: [],
        },
        monthly: {
            type: MonthlyRepeatType.Weekday,
            weekday: {
                weekdayNumber: 1,
                weekday: WeekdayType.Monday,
                monthNumber: 1,
            },
            day: {
                dayNumber: 1,
                monthNumber: 1,
            },
        },
        end: {
            type: RecurrenceEndType.Never,
            date: moment(startDate).add(1, "day")
                .toDate(),
        },
    };
}
